/**
 * VideoStorageProvider — abstração de provedor de storage de vídeo.
 *
 * Default implementation uses the **tus** resumable upload protocol against
 * Supabase Storage's `/storage/v1/upload/resumable` endpoint:
 *
 *   - 6 MB chunks (Supabase recommended size)
 *   - Automatic resume after `ERR_NETWORK_CHANGED` / WiFi drops
 *   - Progress events identical to the previous XHR contract (0–100)
 *
 * The public interface (`upload`, `getPlaybackUrl`, `delete`) is unchanged —
 * callers in `videoService.ts` do not need to be touched.
 *
 * Reference:
 *   - https://supabase.com/docs/guides/storage/uploads/resumable-uploads
 *   - https://github.com/tus/tus-js-client
 */

import * as tus from 'tus-js-client';
import { supabase } from '@/integrations/supabase/client';
import { monitoring } from '@/lib/monitoring';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TRAINING_VIDEOS_BUCKET = 'training-videos';
const DEFAULT_SIGNED_URL_TTL_SECONDS = 3600;
const STORAGE_TUS_REST_PATH = '/storage/v1/upload/resumable';
const DEFAULT_UPLOAD_CONTENT_TYPE = 'application/octet-stream';
const DEFAULT_CACHE_CONTROL_SECONDS = '3600';

// Supabase's tus implementation expects exactly 6 MB chunks.
const TUS_CHUNK_SIZE_BYTES = 6 * 1024 * 1024;

// Retry policy: exponential-ish back-off, up to ~38s of pause across 5 attempts.
// Designed to ride out brief network changes (WiFi ↔ 4G hand-off) without
// surfacing an error to the user.
const TUS_RETRY_DELAYS_MS = [0, 3_000, 5_000, 10_000, 20_000];

const PROGRESS_PERCENT_MIN = 0;
const PROGRESS_PERCENT_MAX = 100;

// Lazy getters so tests can stub `import.meta.env` per-case via vi.stubEnv.
// (A top-level `const` snapshots the value at module load, before stubEnv
// runs.) Production code paths are unaffected — `import.meta.env` is a
// frozen object at runtime so each call is a property read.
const getSupabaseUrl = (): string =>
  (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
const getSupabaseAnonKey = (): string =>
  (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface UploadOptions {
  contentType?: string;
  /** Callback invocado com o percentual atual (0–100) durante o transfer. */
  onProgress?: (percent: number) => void;
}

export interface UploadResult {
  path: string;
  error?: string;
}

export interface VideoStorageProvider {
  /** Faz upload do arquivo e retorna o path/identificador para armazenar no banco. */
  upload(file: File | Blob, path: string, options?: UploadOptions): Promise<UploadResult>;

  /** Gera URL de reprodução (assinada ou pública) a partir do path armazenado. */
  getPlaybackUrl(path: string, expiresInSeconds?: number): Promise<string | null>;

  /** Remove o arquivo do storage. */
  delete(path: string): Promise<{ error?: string }>;

  /** Identificador do provider — útil para logs. */
  readonly name: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const clampPercent = (value: number): number => {
  if (Number.isNaN(value)) return PROGRESS_PERCENT_MIN;
  if (value < PROGRESS_PERCENT_MIN) return PROGRESS_PERCENT_MIN;
  if (value > PROGRESS_PERCENT_MAX) return PROGRESS_PERCENT_MAX;
  return Math.round(value);
};

const parseTusError = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Unknown upload error';
};

/**
 * Extracts structured context from a tus.DetailedError for Sentry.
 *
 * `DetailedError.originalResponse` carries the HTTP status code and body —
 * the most useful signal for diagnosing storage failures in production:
 *   403 → "Invalid Compact JWS" (anon key format) or "exp claim" (expired token)
 *   413 → "Payload Too Large" (bucket file_size_limit exceeded)
 *   404 → bucket not found
 *   500 → Supabase Storage internal error
 *
 * We duck-type the response rather than importing tus types to keep this
 * file testable without the full tus-js-client bundle.
 */
function extractTusErrorContext(error: unknown): Record<string, unknown> {
  if (!(error instanceof Error)) {
    return { kind: typeof error, raw: String(error).slice(0, 200) };
  }

  const ctx: Record<string, unknown> = { message: error.message };

  const resp = (error as unknown as Record<string, unknown>).originalResponse as
    | { getStatus?: () => number; getBody?: () => string }
    | null
    | undefined;

  if (resp) {
    if (typeof resp.getStatus === 'function') {
      ctx.http_status = resp.getStatus();
    }
    if (typeof resp.getBody === 'function') {
      // Cap at 500 chars — response bodies can be large HTML error pages.
      ctx.response_body = resp.getBody().slice(0, 500);
    }
  }

  const cause = (error as unknown as Record<string, unknown>).causingError;
  if (cause instanceof Error) {
    ctx.causing_error = cause.message;
  }

  return ctx;
}

/**
 * A valid JWS in compact serialisation is three base64url-encoded segments
 * separated by dots (header.payload.signature). Anything else — empty
 * string, single-segment, whitespace, opaque string — gets rejected by the
 * Supabase Storage server with `403 { code: "AccessDenied", message:
 * "Invalid Compact JWS" }`. We pre-validate so we never overwrite the
 * existing (working) header with garbage.
 */
const looksLikeJwt = (value: unknown): value is string => {
  if (typeof value !== 'string' || value.length === 0) return false;
  return /^[\w-]+\.[\w-]+\.[\w-]+$/.test(value);
};

/**
 * Builds a Sentry-safe summary of a token without exposing its full value.
 * Used in diagnostic logs when we detect a malformed token so we can tell at
 * a glance whether the cached session was empty, the wrong type, missing the
 * signature segment, etc. — the actual byte sequence stays out of the report.
 */
type TokenShape =
  | { kind: 'undefined' }
  | { kind: 'null' }
  | { kind: 'not_string'; actual: string }
  | {
      kind:         'string';
      length:       number;
      segments:     number;
      prefix:       string;
      suffix:       string;
      looksLikeJwt: boolean;
    };

const describeToken = (value: unknown): TokenShape => {
  if (value === undefined) return { kind: 'undefined' };
  if (value === null)      return { kind: 'null' };
  if (typeof value !== 'string') return { kind: 'not_string', actual: typeof value };
  return {
    kind:         'string',
    length:       value.length,
    segments:     value.split('.').length,
    // First 6 chars cover the standard JWT header prefix `eyJhbG…` plus a
    // bit. Last 4 chars are non-sensitive trailing of the signature segment.
    // Together they're enough to distinguish "undefined" / "null" /
    // truncated / opaque from a real JWT.
    prefix:       value.slice(0, 6),
    suffix:       value.slice(-4),
    looksLikeJwt: looksLikeJwt(value),
  };
};

// ---------------------------------------------------------------------------
// SupabaseStorageProvider — tus-backed
// ---------------------------------------------------------------------------

class SupabaseStorageProvider implements VideoStorageProvider {
  readonly name = 'supabase-tus';

  async upload(file: File | Blob, path: string, options?: UploadOptions): Promise<UploadResult> {
    const { data, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !data.session) {
      return { path: '', error: 'Sessão não encontrada. Faça login novamente.' };
    }

    // The tus client copies `headers` into every request and never validates
    // them. If the cached session's access_token is somehow malformed
    // (undefined, empty, string literal "null", opaque, …) every chunk gets
    // sent with a broken Authorization header and the server returns
    // `403 { code: AccessDenied, message: "Invalid Compact JWS" }`.
    //
    // Defence: validate the initial token shape. If it doesn't look like a
    // JWT, log a Sentry diagnostic and try a single explicit refreshSession()
    // as recovery. If that still doesn't produce a valid JWT, abort with a
    // clear error rather than burning upload time on a doomed transfer.
    let accessToken = data.session.access_token;
    const anonKey   = getSupabaseAnonKey();

    // Defence #1 — apikey (the anon JWT from env).
    //
    // The Supabase Storage `/upload/resumable` endpoint validates the apikey
    // header via `jose.compactVerify()` which requires the JWT compact
    // serialisation (3 base64url segments). It does NOT accept the new
    // `sb_publishable_xxx` key format Supabase introduced in 2024 — that
    // format returns `403 Invalid Compact JWS` against tus even though it
    // works fine against most other Supabase endpoints. Until storage
    // catches up, the env var MUST hold the legacy JWT anon key (the one
    // labelled `anon key` in `supabase status` / Studio → API settings,
    // starts with `eyJ...`).
    if (!looksLikeJwt(anonKey)) {
      const rawAnonKey: string = anonKey as unknown as string;
      const usingNewKeyFormat =
        rawAnonKey.startsWith('sb_publishable_') ||
        rawAnonKey.startsWith('sb_secret_');
      monitoring.captureException(
        new Error(
          usingNewKeyFormat
            ? '[videoStorageProvider] VITE_SUPABASE_ANON_KEY is the new sb_* format; tus needs the legacy JWT anon key'
            : '[videoStorageProvider] VITE_SUPABASE_ANON_KEY is not a JWS — env var misconfigured',
        ),
        { stage: 'apikey_validation', tokenShape: describeToken(anonKey) },
      );
      return {
        path: '',
        error: usingNewKeyFormat
          ? 'A chave configurada é do novo formato `sb_publishable_*`, mas o upload via tus exige a anon key no formato JWT legado. Rode `supabase status` e copie a linha `anon key` (começa com `eyJ`).'
          : 'Configuração inválida (apikey). Verifique VITE_SUPABASE_ANON_KEY no .env.',
      };
    }

    // Defence #2 — the user's access_token (rotates over time).
    if (!looksLikeJwt(accessToken)) {
      monitoring.captureException(
        new Error('[videoStorageProvider] initial access_token is not a JWS — attempting refresh'),
        { stage: 'access_token_validation', tokenShape: describeToken(accessToken) },
      );

      try {
        const refreshed = await supabase.auth.refreshSession();
        if (refreshed.error || !refreshed.data.session) {
          monitoring.captureException(
            new Error('[videoStorageProvider] refreshSession failed after malformed initial token'),
            { stage: 'access_token_refresh', refreshError: refreshed.error?.message ?? null },
          );
          return { path: '', error: 'Sessão inválida. Faça login novamente.' };
        }
        const recovered = refreshed.data.session.access_token;
        if (!looksLikeJwt(recovered)) {
          monitoring.captureException(
            new Error('[videoStorageProvider] token still not a JWS after refreshSession'),
            { stage: 'access_token_refresh', tokenShape: describeToken(recovered) },
          );
          return { path: '', error: 'Sessão inválida. Faça login novamente.' };
        }
        accessToken = recovered;
      } catch (err) {
        monitoring.captureException(err, { stage: 'initial_token_refresh' });
        return { path: '', error: 'Sessão inválida. Faça login novamente.' };
      }
    }

    const contentType = options?.contentType ?? DEFAULT_UPLOAD_CONTENT_TYPE;
    const endpoint = `${getSupabaseUrl()}${STORAGE_TUS_REST_PATH}`;

    return new Promise<UploadResult>((resolve) => {
      const upload = new tus.Upload(file, {
        endpoint,
        retryDelays: TUS_RETRY_DELAYS_MS,
        chunkSize:   TUS_CHUNK_SIZE_BYTES,

        // Do NOT persist a resume fingerprint to localStorage.
        //
        // Each call to uploadTrainingVideo() generates a brand-new unique
        // object path (`${userId}/${Date.now()}.${ext}` — see videoService).
        // tus, however, keys its resume cache on the FILE fingerprint
        // (name + size + type + lastModified + endpoint), which is independent
        // of the path. So a stale localStorage entry from a previously
        // interrupted attempt would make a *retry* resume against the OLD
        // upload URL while we hand the DB the NEW path — either a 404/410 that
        // surfaces as "Falha ao fazer upload do vídeo" or a silent storage/DB
        // mismatch. This is overwhelmingly a mobile problem (backgrounding,
        // wifi↔cellular switches and screen locks interrupt uploads far more
        // often than on desktop). Within a single attempt, `retryDelays`
        // already handles transient network blips; we just don't want to carry
        // a half-finished upload across attempts.
        storeFingerprintForResuming: false,

        // NOTE: do NOT put `authorization` here.
        //
        // `headers` is passed to `XHR.setRequestHeader` as part of tus's
        // request setup. We ALSO set `authorization` inside `onBeforeRequest`
        // (below) so the JWT refreshes per chunk. Per the XHR spec,
        // setRequestHeader called twice for the same header CONCATENATES the
        // values with `, ` — producing `Bearer X, Bearer Y`. Supabase Storage
        // then strips only the first `Bearer ` prefix and tries to parse
        // `X, Bearer Y` as a JWT, which throws `ERR_JWS_INVALID` → "Invalid
        // Compact JWS" with a 403. Keeping `authorization` exclusively in
        // `onBeforeRequest` ensures the header is set exactly once.
        headers: {
          apikey:     anonKey,
          // x-upsert: false matches the previous XHR behaviour — never overwrite
          // an existing object. The folder/timestamp pattern in videoService
          // guarantees uniqueness, so collisions are not expected.
          'x-upsert': 'false',
        },

        // Refresh the JWT before every chunk. Supabase access tokens default
        // to 1h TTL; a slow upload of a large file can easily outlive that
        // window and start getting `403 { code: AccessDenied, message: "exp
        // claim timestamp check failed" }` halfway through. `getSession()`
        // returns the cached token when valid and silently refreshes when it
        // isn't (autoRefreshToken default = true), so the header stays valid
        // for the lifetime of the upload no matter how long it takes.
        //
        // Defensive: we NEVER overwrite the existing (working) auth header
        // unless we have a token that actually looks like a JWS. A malformed
        // value sent here lands as 403 `Invalid Compact JWS` and kills the
        // upload — keeping the original header until we have a strictly
        // better one to swap in is the safe default.
        onBeforeRequest: async (req) => {
          try {
            const { data, error } = await supabase.auth.getSession();
            if (error || !data.session) {
              monitoring.captureException(
                new Error('[videoStorageProvider] JWT refresh skipped: no session'),
                { stage: 'jwt_refresh', cause: 'no_session' },
              );
              return;
            }
            const candidate = data.session.access_token;
            if (!looksLikeJwt(candidate)) {
              monitoring.captureException(
                new Error('[videoStorageProvider] JWT refresh skipped: token does not look like a JWS'),
                { stage: 'jwt_refresh', tokenShape: describeToken(candidate) },
              );
              return;
            }
            req.setHeader('authorization', `Bearer ${candidate}`);
          } catch (err) {
            // Never let JWT refresh failure crash the upload — fall back to
            // the header set on the previous chunk. We capture for visibility.
            monitoring.captureException(err, { stage: 'jwt_refresh' });
          }
        },

        // Required by Supabase: metadata fields tell the resumable endpoint
        // which bucket + object key the chunks belong to.
        metadata: {
          bucketName:   TRAINING_VIDEOS_BUCKET,
          objectName:   path,
          contentType,
          cacheControl: DEFAULT_CACHE_CONTROL_SECONDS,
        },

        // Send a small slice of data with the creation request to skip one RTT
        // for short files.
        uploadDataDuringCreation: true,

        onError: (error) => {
          monitoring.captureException(
            new Error('[videoStorageProvider] TUS upload failed'),
            {
              stage:  'tus_upload_error',
              bucket: TRAINING_VIDEOS_BUCKET,
              path,
              ...extractTusErrorContext(error),
            },
          );
          resolve({ path: '', error: parseTusError(error) });
        },

        onProgress: (bytesUploaded, bytesTotal) => {
          if (!options?.onProgress || bytesTotal <= 0) return;
          const percent = clampPercent((bytesUploaded / bytesTotal) * PROGRESS_PERCENT_MAX);
          options.onProgress(percent);
        },

        onSuccess: () => {
          options?.onProgress?.(PROGRESS_PERCENT_MAX);
          resolve({ path });
        },
      });

      // Always start a fresh upload to this attempt's unique path. We
      // deliberately do NOT call findPreviousUploads()/resumeFromPreviousUpload()
      // — see the storeFingerprintForResuming note above for why resuming
      // across attempts corrupts the path and breaks mobile retries.
      upload.start();
    });
  }

  async getPlaybackUrl(
    path: string,
    expiresInSeconds: number = DEFAULT_SIGNED_URL_TTL_SECONDS
  ): Promise<string | null> {
    if (path.startsWith('http')) return path;

    const { data, error } = await supabase.storage
      .from(TRAINING_VIDEOS_BUCKET)
      .createSignedUrl(path, expiresInSeconds);

    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  }

  async delete(path: string): Promise<{ error?: string }> {
    const { error } = await supabase.storage.from(TRAINING_VIDEOS_BUCKET).remove([path]);
    if (error) return { error: error.message };
    return {};
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

let _instance: VideoStorageProvider | null = null;

export const getVideoStorageProvider = (): VideoStorageProvider => {
  if (!_instance) _instance = new SupabaseStorageProvider();
  return _instance;
};

/** Permite a testes substituírem o singleton. */
export const __setVideoStorageProviderForTests = (provider: VideoStorageProvider | null): void => {
  _instance = provider;
};

// ---------------------------------------------------------------------------
// Audio (mesmo backend, bucket separado)
// ---------------------------------------------------------------------------

const VIDEO_AUDIO_BUCKET = 'video-audio';

export const getSignedAudioUrl = async (
  path: string,
  expiresInSeconds: number = DEFAULT_SIGNED_URL_TTL_SECONDS
): Promise<string | null> => {
  if (path.startsWith('http')) return path;

  const { data, error } = await supabase.storage
    .from(VIDEO_AUDIO_BUCKET)
    .createSignedUrl(path, expiresInSeconds);

  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
};
